import { useState } from 'react'
import { ModerationContentType } from '@ivy-monorepo/shared'
import ConfirmationModal from '../modals/ConfirmationModal'
import WarnUserModal from '../modals/WarnUserModal'
import LockUserModal from '../modals/LockUserModal'
import Icon from '../Icon'
import { adminService } from '../../services/admin.service'

export interface ModerationActionsConfig {
  authorPublicId: string
  contentType: ModerationContentType
  contentReferenceId: string
  quotedContent: string
  isFlagged: boolean
  onFlagChange?: (flagged: boolean) => void
  onDelete?: () => Promise<void>
  deleteConfirmTitle?: string
  deleteConfirmBody?: string
}

interface ModerationActionsBarProps extends ModerationActionsConfig {
  variant?: 'bar' | 'icons'
}

const ModerationActionsBar = ({
  authorPublicId,
  contentType,
  contentReferenceId,
  quotedContent,
  isFlagged,
  onFlagChange,
  onDelete,
  deleteConfirmTitle = 'Delete content?',
  deleteConfirmBody = 'This will hide the content from users. It is retained for audit.',
  variant = 'bar',
}: ModerationActionsBarProps) => {
  const [isFlagPending, setIsFlagPending] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isWarnOpen, setIsWarnOpen] = useState(false)
  const [isLockOpen, setIsLockOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleToggleFlag = async () => {
    try {
      setIsFlagPending(true)
      setActionError(null)
      if (isFlagged) {
        if (contentType === ModerationContentType.VenueBreadcrumb) {
          await adminService.unflagVenueBreadcrumb(contentReferenceId)
        } else if (contentType === ModerationContentType.VenueBreadcrumbReply) {
          await adminService.unflagVenueBreadcrumbReply(contentReferenceId)
        } else if (contentType === ModerationContentType.Sticker) {
          await adminService.unflagSticker(contentReferenceId)
        } else {
          await adminService.unflagMessage(Number.parseInt(contentReferenceId, 10))
        }
        onFlagChange?.(false)
      } else {
        if (contentType === ModerationContentType.VenueBreadcrumb) {
          await adminService.flagVenueBreadcrumb(contentReferenceId)
        } else if (contentType === ModerationContentType.VenueBreadcrumbReply) {
          await adminService.flagVenueBreadcrumbReply(contentReferenceId)
        } else if (contentType === ModerationContentType.Sticker) {
          await adminService.flagSticker(contentReferenceId)
        } else {
          await adminService.flagMessage(Number.parseInt(contentReferenceId, 10))
        }
        onFlagChange?.(true)
      }
    } catch {
      setActionError('Failed to update flag.')
    } finally {
      setIsFlagPending(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return false
    try {
      setIsDeleting(true)
      await onDelete()
      return true
    } finally {
      setIsDeleting(false)
    }
  }

  const flagLabel = isFlagged ? 'Unflag' : 'Flag'
  const flagTooltip = isFlagged ? 'Remove flag from this content' : 'Flag this content for review'

  return (
    <>
      {variant === 'icons' ? (
        <div className="moderation-icon-actions">
          <button
            type="button"
            className={`moderation-icon-button${isFlagged ? ' is-active' : ''}`}
            onClick={handleToggleFlag}
            disabled={isFlagPending}
            title={flagTooltip}
            aria-label={flagTooltip}
          >
            <Icon name="Flag" />
          </button>
          {onDelete && (
            <button
              type="button"
              className="moderation-icon-button"
              onClick={() => setIsDeleteConfirmOpen(true)}
              disabled={isDeleting}
              title="Delete this content"
              aria-label="Delete this content"
            >
              <Icon name="TrashCan" />
            </button>
          )}
          <button
            type="button"
            className="moderation-icon-button"
            onClick={() => setIsWarnOpen(true)}
            title="Warn the author"
            aria-label="Warn the author"
          >
            <Icon name="TriangleExclamation" />
          </button>
          <button
            type="button"
            className="moderation-icon-button"
            onClick={() => setIsLockOpen(true)}
            title="Ban the author"
            aria-label="Ban the author"
          >
            <Icon name="Ban" />
          </button>
        </div>
      ) : (
        <div className="moderation-actions-bar">
          <button
            type="button"
            className={`styled-button ${isFlagged ? 'moderation-flagged' : ''}`}
            onClick={handleToggleFlag}
            disabled={isFlagPending}
          >
            {flagLabel}
          </button>
          {onDelete && (
            <button type="button" className="styled-button" onClick={() => setIsDeleteConfirmOpen(true)} disabled={isDeleting}>
              Delete
            </button>
          )}
          <button type="button" className="styled-button" onClick={() => setIsWarnOpen(true)}>
            Warn
          </button>
          <button type="button" className="styled-button" onClick={() => setIsLockOpen(true)}>
            Ban
          </button>
        </div>
      )}
      {actionError && <div className="page-error">{actionError}</div>}

      {onDelete && (
        <ConfirmationModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          title={deleteConfirmTitle}
          confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
          cancelLabel="Cancel"
          isLoading={isDeleting}
          onConfirm={handleDelete}
        >
          <div>{deleteConfirmBody}</div>
        </ConfirmationModal>
      )}

      <WarnUserModal
        isOpen={isWarnOpen}
        onClose={() => setIsWarnOpen(false)}
        userPublicId={authorPublicId}
        contentType={contentType}
        contentReferenceId={contentReferenceId}
        quotedContent={quotedContent}
      />

      <LockUserModal
        isOpen={isLockOpen}
        onClose={() => setIsLockOpen(false)}
        publicId={authorPublicId}
        updateUser={() => {}}
      />
    </>
  )
}

export default ModerationActionsBar
